import { useEffect, useState } from 'react';
import {
  Animated,
  Easing,
  type LayoutChangeEvent,
  Platform,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';

interface ShimmerSkeletonProps {
  width?: number | string;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
}

type WebShimmerStyle = ViewStyle & { backgroundImage?: string };

const shimmerClock = new Animated.Value(0);
let shimmerAnimation: Animated.CompositeAnimation | null = null;
let shimmerSubscribers = 0;

function subscribeToShimmerClock() {
  shimmerSubscribers += 1;

  if (shimmerSubscribers === 1) {
    shimmerClock.setValue(0);
    shimmerAnimation = Animated.loop(
      Animated.timing(shimmerClock, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    shimmerAnimation.start();
  }

  return () => {
    shimmerSubscribers -= 1;
    if (shimmerSubscribers === 0) {
      shimmerAnimation?.stop();
      shimmerAnimation = null;
    }
  };
}

export function ShimmerSkeleton({
  width = '100%',
  height = 20,
  borderRadius = 8,
  style,
}: Readonly<ShimmerSkeletonProps>) {
  const reducedMotion = useReducedMotion();
  const [hostWidth, setHostWidth] = useState(0);

  useEffect(() => {
    if (reducedMotion) return;
    return subscribeToShimmerClock();
  }, [reducedMotion]);

  const dimensions = {
    width: width as number,
    height,
    borderRadius,
  };

  if (Platform.OS !== 'web') {
    const opacity = shimmerClock.interpolate({
      inputRange: [0, 0.5, 1],
      outputRange: [0.72, 1, 0.72],
    });

    return (
      <Animated.View
        style={[styles.skeletonBase, dimensions, !reducedMotion && { opacity }, style]}
      />
    );
  }

  const handleLayout = (event: LayoutChangeEvent) => {
    setHostWidth(event.nativeEvent.layout.width);
  };
  const translateX = shimmerClock.interpolate({
    inputRange: [0, 1],
    outputRange: [-hostWidth, hostWidth],
  });

  return (
    <View onLayout={handleLayout} style={[styles.skeletonBase, dimensions, style]}>
      {!reducedMotion && hostWidth > 0 ? (
        <Animated.View
          style={[styles.shimmer, { width: hostWidth, transform: [{ translateX }] }]}
        />
      ) : null}
    </View>
  );
}

interface SkeletonTextProps {
  lines?: number;
  lineHeight?: number;
  lastLineWidth?: string;
  style?: ViewStyle;
}

export function SkeletonText({
  lines = 3,
  lineHeight = 16,
  lastLineWidth = '60%',
  style,
}: Readonly<SkeletonTextProps>) {
  return (
    <View style={[styles.textContainer, style]}>
      {Array.from({ length: lines }).map((_, index) => {
        const lineId = `skeleton-line-${String(index)}-of-${String(lines)}`;
        return (
          <ShimmerSkeleton
            key={lineId}
            width={index === lines - 1 ? lastLineWidth : '100%'}
            height={lineHeight}
            borderRadius={4}
          />
        );
      })}
    </View>
  );
}

interface SkeletonCardProps {
  style?: ViewStyle;
  showIcon?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  iconSize?: number;
  centered?: boolean;
}

export function SkeletonCard({
  style,
  showIcon = true,
  showTitle = true,
  showDescription = true,
  iconSize = 48,
  centered = false,
}: Readonly<SkeletonCardProps>) {
  return (
    <View style={[styles.card, centered && styles.cardCentered, style]}>
      {showIcon && (
        <ShimmerSkeleton
          width={iconSize}
          height={iconSize}
          borderRadius={12}
          style={
            centered ? { ...styles.iconSkeleton, ...styles.iconCentered } : styles.iconSkeleton
          }
        />
      )}
      {showTitle && (
        <ShimmerSkeleton
          width={centered ? '70%' : '80%'}
          height={20}
          borderRadius={6}
          style={
            centered ? { ...styles.titleSkeleton, ...styles.titleCentered } : styles.titleSkeleton
          }
        />
      )}
      {showDescription && (
        <SkeletonText
          lines={2}
          lineHeight={14}
          lastLineWidth="75%"
          style={centered ? styles.descriptionCentered : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  skeletonBase: {
    backgroundColor: colors.border.muted,
    overflow: 'hidden',
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    ...Platform.select({
      web: {
        // Long-form `backgroundImage` — react-native-web rejects the CSS
        // shorthand `background` with "Invalid style property of 'background'".
        backgroundImage: `linear-gradient(90deg, transparent, ${colors.shimmer.highlight}, transparent)`,
      } as WebShimmerStyle,
      default: {},
    }),
  },
  textContainer: {
    gap: spacing.sm,
  },
  card: {
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: spacing.xl,
  },
  cardCentered: {
    alignItems: 'center',
  },
  iconSkeleton: {
    marginBottom: spacing.lg,
  },
  iconCentered: {
    alignSelf: 'center',
  },
  titleSkeleton: {
    marginBottom: spacing.md,
  },
  titleCentered: {
    alignSelf: 'center',
  },
  descriptionCentered: {
    alignItems: 'center',
    width: '100%',
  },
});
